import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { ChannelVisibility, ChannelInvite, ChannelAccessRequest } from 'types/channel';
import type { PeopleSearchResult } from '../../../api/marketplaceApi';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import type { StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import SpinnerAtom from '../../atoms/Spinner/SpinnerAtom';
import PeopleSearchMolecule from '../people-search/PeopleSearchMolecule';
import './ChannelVisibilityAccessMolecule.scss';

// ============================================================
// Props
// ============================================================

export interface ChannelVisibilityAccessMoleculeProps {
  visibility: ChannelVisibility;
  shareToken?: string | null;
  invites: ChannelInvite[];
  onVisibilityChange: (v: ChannelVisibility) => void;
  onRotateToken: () => Promise<void>;
  /** Called with a lowercased email address when the owner invites a person. */
  onInvite: (email: string) => Promise<void>;
  /** Called with the invite's UUID when the owner revokes an invite. */
  onRevokeInvite: (inviteId: string) => Promise<void>;
  /** Called with the invite's UUID when the owner resends a pending invite. */
  onResendInvite?: (inviteId: string) => Promise<void>;
  /** Pending access requests to display for the owner's approve/deny UI. */
  accessRequests?: ChannelAccessRequest[];
  /** Called with the request's UUID when the owner approves an access request. */
  onApproveRequest?: (requestId: string) => Promise<void>;
  /** Called with the request's UUID when the owner denies an access request. */
  onDenyRequest?: (requestId: string) => Promise<void>;
  isMutating?: boolean;
  className?: string;
}

const VISIBILITY_OPTIONS: ChannelVisibility[] = ['private', 'unlisted', 'public'];

const VISIBILITY_ICONS: Record<ChannelVisibility, string> = {
  private: 'pi-lock',
  unlisted: 'pi-eye-slash',
  public: 'pi-globe',
};

function inviteStatusToTone(status: ChannelInvite['status']): StatusBadgeTone {
  switch (status) {
    case 'accepted': return 'success';
    case 'pending': return 'warn';
    case 'declined': return 'danger';
    case 'revoked': return 'neutral';
  }
}

// ============================================================
// Component
// ============================================================

const ChannelVisibilityAccessMolecule: React.FC<ChannelVisibilityAccessMoleculeProps> = ({
  visibility,
  shareToken,
  invites,
  onVisibilityChange,
  onRotateToken,
  onInvite,
  onRevokeInvite,
  onResendInvite,
  accessRequests,
  onApproveRequest,
  onDenyRequest,
  isMutating = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const ns = 'marketplace.channelManage.v3Audience';

  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const excludeEmails = useMemo(
    () => invites.map((i) => (i.email ?? '').toLowerCase()).filter(Boolean),
    [invites]
  );
  const excludeIdentityIds = useMemo(
    () => invites.map((i) => i.identity_id).filter((id): id is string => !!id),
    [invites]
  );

  const inviteLabels = useMemo(
    () => ({
      searchPlaceholder: t(`${ns}.invites.emailPlaceholder`),
      addButton: t(`${ns}.invites.addButton`),
      noMatch: (query: string) => t(`${ns}.invites.noMatch`, { query }),
      alreadyAdded: t(`${ns}.invites.alreadyInvited`),
      sendButton: t(`${ns}.invites.sendInvite`),
      sending: t(`${ns}.invites.sending`),
    }),
    [t]
  );

  const handleAddInvite = useCallback(
    async (person: PeopleSearchResult): Promise<boolean> => {
      try {
        await onInvite((person.email ?? '').toLowerCase());
        return true;
      } catch {
        return false;
      }
    },
    [onInvite]
  );

  const handleRotate = useCallback(async () => {
    setRotating(true);
    try {
      await onRotateToken();
    } finally {
      setRotating(false);
    }
  }, [onRotateToken]);

  const handleCopy = useCallback(() => {
    if (!shareToken) return;
    const url = `${globalThis.location.origin}/marketplace/join?token=${encodeURIComponent(shareToken)}`;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareToken]);

  const acceptedCount = invites.filter((i) => i.status === 'accepted').length;
  const pendingCount = invites.filter((i) => i.status === 'pending').length;

  return (
    <div className={`channel-visibility-access-molecule ${className}`}>
      {/* Visibility picker */}
      <div className="channel-visibility-access-molecule__section">
        <p className="channel-visibility-access-molecule__section-subtitle">
          {t(`${ns}.visibility.subtitle`)}
        </p>
        <div
          className="channel-visibility-access-molecule__picker-grid"
          role="radiogroup"
          aria-label={t(`${ns}.visibility.sectionTitle`)}
        >
          {VISIBILITY_OPTIONS.map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={visibility === v}
              className={`channel-visibility-access-molecule__picker-card${visibility === v ? ' is-selected' : ''}`}
              onClick={() => onVisibilityChange(v)}
              disabled={isMutating}
            >
              <div className="channel-visibility-access-molecule__picker-header">
                <span className={`channel-visibility-access-molecule__picker-radio${visibility === v ? ' is-selected' : ''}`} aria-hidden="true" />
                <i
                  className={`pi ${VISIBILITY_ICONS[v]} channel-visibility-access-molecule__picker-icon`}
                  aria-hidden="true"
                />
                <span className="channel-visibility-access-molecule__picker-label">
                  {t(`${ns}.visibility.${v}.label`)}
                </span>
              </div>
              <span className="channel-visibility-access-molecule__picker-desc">
                {t(`${ns}.visibility.${v}.description`)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Divider + sub-header (Share & invite / Marketplace settings) */}
      {(visibility === 'private' || visibility === 'unlisted') && (
        <div className="channel-visibility-access-molecule__sub-header">
          <i className="pi pi-user-plus channel-visibility-access-molecule__sub-header-icon" aria-hidden="true" />
          {t(`${ns}.visibility.shareAndInviteHeader`)}
        </div>
      )}
      {visibility === 'public' && (
        <div className="channel-visibility-access-molecule__sub-header">
          <i className="pi pi-sliders-h channel-visibility-access-molecule__sub-header-icon" aria-hidden="true" />
          {t(`${ns}.visibility.marketplaceSettingsHeader`)}
        </div>
      )}

      {/* Share link (shown for unlisted/private) */}
      {(visibility === 'unlisted' || visibility === 'private') && (
        <div className="channel-visibility-access-molecule__section">
          <h4 className="channel-visibility-access-molecule__share-link-title">
            {t(`${ns}.shareLink.title`)}
          </h4>
          <div className="channel-visibility-access-molecule__share-link-row">
            <div className="channel-visibility-access-molecule__share-link-field">
              <i className="pi pi-link channel-visibility-access-molecule__share-link-field-icon" aria-hidden="true" />
              <span className="channel-visibility-access-molecule__share-link-preview">
                {shareToken
                  ? `${globalThis.location?.origin ?? 'https://center.me'}/marketplace/join?invite=${shareToken.slice(0, 8)}`
                  : t(`${ns}.shareLink.notGenerated`)}
              </span>
            </div>
            <button
              type="button"
              className="channel-visibility-access-molecule__copy-btn"
              onClick={handleCopy}
              disabled={!shareToken || isMutating}
            >
              <i className="pi pi-copy" aria-hidden="true" />
              {copied ? t(`${ns}.shareLink.copied`) : t(`${ns}.shareLink.copy`)}
            </button>
            <button
              type="button"
              className="channel-visibility-access-molecule__copy-btn channel-visibility-access-molecule__rotate-btn"
              onClick={() => void handleRotate()}
              disabled={isMutating || rotating}
            >
              {rotating ? <SpinnerAtom size="small" /> : <i className="pi pi-refresh" aria-hidden="true" />}
              {t(`${ns}.shareLink.rotate`)}
            </button>
          </div>
          {(visibility === 'private' || shareToken) && (
            <p className="channel-visibility-access-molecule__share-link-hint">
              {t(`${ns}.shareLink.linkHint`)}
            </p>
          )}
        </div>
      )}

      {/* Invite people (private channels) */}
      {visibility === 'private' && (
        <div className="channel-visibility-access-molecule__section">
          <h4 className="channel-visibility-access-molecule__invite-title">
            {t(`${ns}.invites.title`)}
          </h4>

          {/* Generic people picker — shared with the publisher-team add flow. */}
          <PeopleSearchMolecule
            onSelect={handleAddInvite}
            onSubmitRaw={(raw) => handleAddInvite({ email: raw } as PeopleSearchResult)}
            inputIcon="pi-envelope"
            isMutating={isMutating}
            labels={inviteLabels}
            excludeIdentityIds={excludeIdentityIds}
            excludeEmails={excludeEmails}
          />

          {/* Invite list */}
          {invites.length > 0 && (
            <>
              <p className="channel-visibility-access-molecule__count-header">
                {t(`${ns}.invites.countHeader`, { accepted: acceptedCount, pending: pendingCount })}
              </p>
              <ul className="channel-visibility-access-molecule__invites-list">
                {invites.map((invite) => (
                  <li
                    key={invite.id}
                    className="channel-visibility-access-molecule__invite-row"
                  >
                    <span className="channel-visibility-access-molecule__invite-label">
                      {invite.email ?? invite.identity_id ?? ''}
                    </span>
                    <StatusBadgeAtom
                      tone={inviteStatusToTone(invite.status)}
                      label={t(`${ns}.invites.status.${invite.status}`)}
                      data-testid={`invite-status-${invite.id}`}
                    />
                    {invite.status === 'pending' && onResendInvite && (
                      <button
                        type="button"
                        className="channel-visibility-access-molecule__resend-btn"
                        onClick={() => void onResendInvite(invite.id)}
                        disabled={isMutating}
                        aria-label={t(`${ns}.invites.resend`)}
                      >
                        {t(`${ns}.invites.resend`)}
                      </button>
                    )}
                    <button
                      type="button"
                      className="channel-visibility-access-molecule__revoke-btn"
                      onClick={() => void onRevokeInvite(invite.id)}
                      disabled={isMutating}
                      aria-label={t(`${ns}.invites.revoke`)}
                    >
                      {t(`${ns}.invites.revoke`)}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Access requests (owner side — private channel only) */}
      {visibility === 'private' && accessRequests && accessRequests.length > 0 && onApproveRequest && onDenyRequest && (
        <div className="channel-visibility-access-molecule__section">
          <h4 className="channel-visibility-access-molecule__section-title">
            {t(`${ns}.accessRequests.title`)}
          </h4>
          <ul className="channel-visibility-access-molecule__access-requests-list">
            {accessRequests.map((req) => (
              <li
                key={req.id}
                className="channel-visibility-access-molecule__access-request-row"
              >
                <span className="channel-visibility-access-molecule__access-request-label">
                  {req.note
                    ? req.note
                    : t(`${ns}.accessRequests.requestedBy`, { id: req.requester_identity_id })}
                </span>
                <button
                  type="button"
                  className="channel-visibility-access-molecule__approve-btn"
                  onClick={() => void onApproveRequest(req.id)}
                  disabled={isMutating}
                  aria-label={t(`${ns}.accessRequests.approve`)}
                >
                  {t(`${ns}.accessRequests.approve`)}
                </button>
                <button
                  type="button"
                  className="channel-visibility-access-molecule__deny-btn"
                  onClick={() => void onDenyRequest(req.id)}
                  disabled={isMutating}
                  aria-label={t(`${ns}.accessRequests.deny`)}
                >
                  {t(`${ns}.accessRequests.deny`)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

ChannelVisibilityAccessMolecule.displayName = 'ChannelVisibilityAccessMolecule';

export default ChannelVisibilityAccessMolecule;
